<template>
  <div>
    <div class="input-token--row">
      <input
        v-for="(n, index) in length"
        :key="index"
        type="number"
        class="input-token--row--field"
        ref="inputList"
        :autofocus="index === 0"
        @input="handleInput($event, index)"
        @keydown="handleKeyDown($event, index)"
        :disabled="isLoading || isValid"
      />
    </div>

    <div v-if="status" class="input-token--message" :class="[status]">
      <inline-spinner
        v-if="isLoading"
        :border="3"
        :width="20"
        :height="20"
        :primaryColor="loadingPrimaryColor"
        :secondaryColor="loadingSecondaryColor"
      />
      <check-circle-icon size="20" color="#15803D" v-else-if="isValid"/>
      <minus-circle-icon size="20" color="#B91C1C" v-else />
      <span class="small-regular">{{ statusText }}</span>
    </div>
  </div>
</template>

<script>
import { CheckCircleIcon, MinusCircleIcon } from '@vue-hero-icons/solid';
import InlineSpinner from '@/components/common/InlineSpinner.vue';

export default {
  name: 'input-token',
  components: {
    CheckCircleIcon,
    MinusCircleIcon,
    InlineSpinner,
  },
  props: {
    length: {
      type: Number,
      default: 6,
    },
    status: {
      type: String,
    },
    value: String,
    loadingPrimaryColor: {
      type: String,
      default: '#94A3B8',
    },
    loadingSecondaryColor: {
      type: String,
      default: '#E2E8F0',
    },
    loadingMessage: {
      type: String,
      default: 'Verificando...',
    },
    successMessage: {
      type: String,
      default: 'Código válido',
    },
    failureMessage: {
      type: String,
      default: 'Código inválido',
    },
  },
  data: () => ({
    valueArray: [],
  }),
  computed: {
    isLoading() {
      return this.status === 'loading';
    },
    isValid() {
      return this.status === 'success';
    },
    statusText() {
      if (this.isLoading) return this.loadingMessage;
      if (this.isValid) return this.successMessage;
      return this.failureMessage;
    },
  },
  methods: {
    stringToArray: (value) => value.split(''),
    arrayToString: (value) => value.join(''),
    isComplete() {
      const index = this.valueArray.find((value) => value === '');
      return index === undefined;
    },
    previousIndex(currentIndex) {
      return currentIndex <= 0 ? 0 : currentIndex - 1;
    },
    nextIndex(currentIndex) {
      return currentIndex >= this.length - 1
        ? this.length - 1
        : currentIndex + 1;
    },
    updateInputValue(value) {
      this.valueArray = value;
      this.$refs.inputList.forEach((input, index) => {
        // eslint-disable-next-line no-param-reassign
        input.value = this.valueArray[index] || '';
      });
    },
    updateInputFocus(index) {
      this.$refs.inputList[index].focus();
    },
    handleInput(event, inputIndex) {
      if (!event.target.value) return;
      let currentInputIndex = inputIndex;
      let inputDataIndex = 0;
      let inputData = event.target.value;
      if (event.inputType === 'insertText') {
        inputData = event.data;
      }

      const inputDataArray = this.stringToArray(inputData);
      const currentValueArray = this.valueArray.map((value, index) => {
        if (index < inputIndex || inputDataIndex >= inputDataArray.length) {
          return value;
        }
        currentInputIndex = this.nextIndex(currentInputIndex);
        inputDataIndex += 1;
        return inputDataArray[inputDataIndex - 1];
      });

      this.updateInputValue(currentValueArray);
      this.updateInputFocus(currentInputIndex);

      this.$emit('input', this.arrayToString(this.valueArray));
      if (this.isComplete()) {
        this.$emit('complete', this.arrayToString(this.valueArray));
      }
    },
    handleKeyDown(event, inputIndex) {
      let currentInputIndex = inputIndex;
      const currentValueArray = [...this.valueArray];
      switch (event.key) {
        case 'ArrowLeft':
          this.updateInputFocus(this.previousIndex(inputIndex));
          break;
        case 'ArrowRight':
          this.updateInputFocus(this.nextIndex(inputIndex));
          break;
        case 'Backspace':
          event.preventDefault();
          if (currentValueArray[currentInputIndex] === '') {
            currentInputIndex = this.previousIndex(currentInputIndex);
            currentValueArray[currentInputIndex] = '';
          } else {
            currentValueArray[currentInputIndex] = '';
            currentInputIndex = this.previousIndex(currentInputIndex);
          }
          this.updateInputValue(currentValueArray);
          this.updateInputFocus(currentInputIndex);
          currentValueArray[inputIndex] = '';
          this.$emit('input', this.arrayToString(this.valueArray));
          this.$emit('backspace', currentInputIndex);
          break;
        default:
          break;
      }
    },
    cleanToken() {
      this.valueArray = [];
      for (let index = 0; index < this.length; index += 1) {
        this.valueArray.push('');
      }
      this.$refs.inputList.forEach((input) => {
        // eslint-disable-next-line no-param-reassign
        input.value = '';
      });
      this.updateInputFocus(0);
    },
  },
  mounted() {
    for (let index = 0; index < this.length; index += 1) {
      this.valueArray.push('');
    }
    this.updateInputFocus(0);
  },
};
</script>

<style lang="scss" scoped>
  @import '../../scss/__new-styles.scss';

  .input-token--row {
    display: flex;
    align-content: center;
    justify-content: center;
    gap: 12px;

    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    input[type=number] {
      -moz-appearance: textfield;
    }
  }

  .input-token--row--field {
    text-align: center;
    width: 42px;
    height: 42px;
  }

  .input-token--message {
    display: flex;
    justify-content: center;
    gap: 8px;
    align-items: center;
    margin-top: 20px;

    &.loading {
      span {
        color: $gray-500;
      }
    }

    &.success {
      span {
        color: $green-700;
      }
    }

    &.failure {
      span {
        color: $red-700;
      }
    }
  }
</style>
